import { type RankingItem } from '../types';

const K_FACTOR = 32; // Elo rating system constant

/**
 * Calculate expected score for item1 against item2
 */
function expectedScore(rating1: number, rating2: number): number {
  return 1 / (1 + Math.pow(10, (rating2 - rating1) / 400));
}

/**
 * Update Elo ratings after a comparison
 */
export function updateRatings(
  item1: RankingItem,
  item2: RankingItem,
  winnerId: string
): { item1: RankingItem; item2: RankingItem } {
  const item1Won = winnerId === item1.id;
  const actualScore = item1Won ? 1 : 0;
  
  const expected1 = expectedScore(item1.score, item2.score);
  const expected2 = expectedScore(item2.score, item1.score);
  
  const newScore1 = item1.score + K_FACTOR * (actualScore - expected1);
  const newScore2 = item2.score + K_FACTOR * ((1 - actualScore) - expected2);
  
  return {
    item1: {
      ...item1,
      score: Math.round(newScore1),
      wins: item1Won ? item1.wins + 1 : item1.wins,
      losses: item1Won ? item1.losses : item1.losses + 1,
      comparisons: item1.comparisons + 1,
    },
    item2: {
      ...item2,
      score: Math.round(newScore2),
      wins: item1Won ? item2.wins : item2.wins + 1,
      losses: item1Won ? item2.losses + 1 : item2.losses,
      comparisons: item2.comparisons + 1,
    },
  };
}

/**
 * Generate all possible pairwise comparisons
 */
export function generateComparisons(items: RankingItem[]): Array<[string, string]> {
  const comparisons: Array<[string, string]> = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      comparisons.push([items[i].id, items[j].id]);
    }
  }
  return comparisons;
}

/**
 * Sort items by score (descending)
 */
export function sortByRank(items: RankingItem[]): RankingItem[] {
  return [...items].sort((a, b) => b.score - a.score);
}

